/**
 * CORE-M-08 Budget Reallocation tab body — phase A.
 *
 * Chans's 2026-09-15 rebuild spec §2.8: the most complex tab. Art's existing
 * budget-move logic surfaced properly, editable amounts, heavy context per
 * row, snooze expected. The plan doc (rebuild-implementation-plan-2026-09-16
 * §CORE-M-08) breaks that down and flags M-08 as effort L, blocked in part
 * on the R-2 verbs ruling from Chans.
 *
 * This phase A does the lift-and-shift into the tab body, per the same
 * pattern as M-03 for Negative Keyword Suggestions:
 *
 *   - The existing 1119-line <BudgetReallocationTab> in
 *     `components/google-ads/` is the operator's daily driver today; its
 *     data path, snapshot query, money strip, platform breakdown, policy
 *     panel, campaign table and history are shipped as-is.
 *   - No new backend, no editable amount override, no evidence-chip
 *     redesign yet — that lands in phase B once R-2 is closed.
 *   - The count badge stays at zero for this tab. Phase B introduces the
 *     "N pending" counter once Apply / Ignore / Snooze verbs make a backlog
 *     meaningful.
 *
 * The file name mirrors the spec's tab label (bus factor, plan doc §8).
 */
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BudgetReallocationTab as InnerBudgetReallocationTab } from '@/components/google-ads/BudgetReallocationTab';
import { useTenantStore } from '@/stores/useTenantStore';

interface BudgetReallocationTabProps {
  tenantId?: string | null;
}

export function BudgetReallocationTab({
  tenantId: tenantIdProp,
}: BudgetReallocationTabProps) {
  const storeTenant = useTenantStore((s) => s.currentTenant);
  const tenantId = (tenantIdProp === undefined ? storeTenant : tenantIdProp) ?? null;

  if (!tenantId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No workspace selected</CardTitle>
          <CardDescription>
            Pick a workspace to see its budget pacing and reallocation moves.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <InnerBudgetReallocationTab
      tenantId={tenantId}
      renderContext="management-subtab"
    />
  );
}

export default BudgetReallocationTab;
